import { useLingui } from "@lingui/react/macro";
import { List, Text, Title } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";

import { DocHeading } from "@/components/docs/DocHeading.tsx";
import { DocImage } from "@/components/docs/DocImage.tsx";
import { DocsContent } from "@/components/docs/DocsContent.tsx";
import resultEn from "@/docs/images/property-search/result.en.webp";
import resultSv from "@/docs/images/property-search/result.sv.webp";

export const Route = createFileRoute(
  "/docs/property-search/property-information",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useLingui();

  return (
    <DocsContent>
      <Title order={1} size="h2">{t`Viewing property information`}</Title>
      <Text>
        {t`After purchasing data in the final step of Property Search, you will be taken to a page displaying all the information
         associated with the selected datasets and properties. Here, you can also download the data in various formats. The property extract is shown for one property at a time. If you
         searched for several properties, use the "Shows data for" list to switch between them.`}
      </Text>

      <DocImage
        src={{ sv: resultSv, en: resultEn }}
        alt={t`Property information`}
        height={600}
      />

      <Text>
        {t`Property extracts are cached for 14 days, which means that you always can return to them within that period. If the data was retrieved more than an hour ago, a notice is shown at the top of the page. Make a new extract if you need to be sure that the information is up to date.`}
      </Text>

      <DocHeading order={2} size="h3">{t`Downloads`}</DocHeading>
      <Text>
        {t`The download buttons on the result page always include all properties in the search, not just the property currently shown.`}
      </Text>

      <DocHeading order={3} size="h4">{t`PDF report`}</DocHeading>
      <Text>
        {t`Click "Download report in PDF format" next to the Property Extract heading to download a zip file containing one PDF report per property. The reports summarise the property extract and are suitable for attaching to documentation or sharing within the assignment.`}
      </Text>

      <DocHeading
        order={3}
        size="h4"
      >{t`Address labels for mailing`}</DocHeading>
      <Text>
        {t`Downloads a CSV file with the names and postal addresses of the property owners. The file can be used for mail merges or for printing address labels, for example when sending information to affected landowners.`}
      </Text>

      <DocHeading order={3} size="h4">{t`Landowner register`}</DocHeading>
      <Text>
        {t`Downloads a CSV file listing the owners of each property, including personal identity numbers. Only use this register when personal identity numbers are actually required for the assignment.`}
      </Text>

      <DocHeading
        order={3}
        size="h4"
      >{t`Landowner register (GDPR)`}</DocHeading>
      <Text>
        {t`Downloads the same landowner register but without personal identity numbers. This is the recommended option in most cases, since it minimises the amount of personal data that is processed and stored.`}
        {t`Landowner registers and address labels contain personal data. Store the files in an approved Sweco location, do not share them outside the assignment and delete them when they are no longer needed.`}
      </Text>
      <DocHeading order={3} size="h4">{t`GeoPackage`}</DocHeading>
      <Text>
        {t`A GeoPackage download of the properties, for use in GIS applications, is planned. The button is shown on the result page but is not yet available.`}
      </Text>

      <DocHeading order={2} size="h3">{t`Property Extract`}</DocHeading>
      <Text>
        {t`The property extract is divided into tabs, one for each dataset. Tabs for datasets that were not selected in the search are disabled. To view that information, make a new search that includes the dataset.`}
      </Text>

      <DocHeading order={3} size="h4">{t`General section`}</DocHeading>
      <Text>
        {t`Shows general information from the general section of the real property register, such as:`}
      </Text>
      <List withPadding>
        <List.Item>{t`Total area, divided into land area and water area.`}</List.Item>
        <List.Item>{t`Property formation measures, with dossier and date.`}</List.Item>
        <List.Item>{t`The origin of the property.`}</List.Item>
        <List.Item>{t`Previous designations.`}</List.Item>
      </List>

      <DocHeading order={3} size="h4">{t`Designations`}</DocHeading>
      <Text>
        {t`Shows the current property designation together with the municipality code and the property's unique identifier (UUID). It also shows when the general section and the registration section of the register were last changed.`}
      </Text>

      <DocHeading order={3} size="h4">{t`Addresses`}</DocHeading>
      <Text>
        {t`Lists the addresses associated with the property, including the municipality part and the coordinates of each address in SWEREF99 TM.`}
      </Text>

      <DocHeading order={3} size="h4">{t`Owner & Mortgages`}</DocHeading>
      <Text>
        {t`Shows information from the registration section of the real property register:`}
      </Text>
      <List withPadding>
        <List.Item>{t`Current owners, their share of the property and the registration date of the title.`}</List.Item>
        <List.Item>{t`Related acquisitions.`}</List.Item>
        <List.Item>{t`Registered mortgages with amount and order, as well as the total number and total amount of mortgages.`}</List.Item>
      </List>

      <DocHeading order={3} size="h4">
        {t({ message: `Land regulation`, context: `docs` })}
      </DocHeading>
      <Text>
        {t`Shows zoning plans and other land regulations that affect the property, for example:`}
      </Text>
      <List withPadding>
        <List.Item>{t`Plans and planning regulations.`}</List.Item>
        <List.Item>{t`Nature conservation.`}</List.Item>
        <List.Item>{t`Ancient monuments and antiquarian assessments.`}</List.Item>
        <List.Item>{t`Mineral rights.`}</List.Item>
        <List.Item>{t`Investigations and other regulations.`}</List.Item>
      </List>

      <DocHeading order={3} size="h4">{t`Right`}</DocHeading>
      <Text>
        {t`Lists rights that encumber or benefit the property, such as contractual easements, official easements, utility easements and official usufructs. For each right, you can see whether the property is the benefiting or burdened party, the purpose of the right and its legal state.`}
      </Text>

      <DocHeading order={3} size="h4">{t`Taxation`}</DocHeading>
      <Text>
        {t`Shows the taxation units connected to the property, grouped by type, for example house, rental, farm or industry taxation units. For each taxation unit, the following is shown:`}
      </Text>
      <List withPadding>
        <List.Item>{t`Tax assessment number, taxation year, property type and type code.`}</List.Item>
        <List.Item>{t`Tax assessment value, divided into land value and building value.`}</List.Item>
        <List.Item>{t`Taxed owners and their shares.`}</List.Item>
        <List.Item>{t`Valuation units, such as buildings and land, with their individual values.`}</List.Item>
      </List>
    </DocsContent>
  );
}
